import * as THREE from 'three';
import { ReactiveProperty } from '@pointorama/potree-shared';
import type { MaterialOverride, ClipOverride } from './RenderView';
import type { ClipState } from './buildClipState';
import { filterClipStateByMask } from './buildClipState';

/**
 * One layered setting cell.
 *
 * `base` is the default value for this view; `override` is an optional
 * temporary overlay. Resolved value = `override.value ?? base.value`.
 *
 * Both halves are ReactiveProperty so React can subscribe granularly: a
 * widget UI can show/edit base in one tab and override in another, with
 * each tab re-rendering only when its own cell changes.
 */
export interface SettingsCell<T> {
  readonly base: ReactiveProperty<T>;
  /** Override value. `undefined` = no override active; resolved value falls back to base. */
  readonly override: ReactiveProperty<T | undefined>;
}

/** Construct a settings cell with the given base value and no override. */
export function makeCell<T>(initialBase: T): SettingsCell<T> {
  return {
    base: new ReactiveProperty<T>(initialBase),
    override: new ReactiveProperty<T | undefined>(undefined),
  };
}

/** Resolve a cell to its current effective value. */
export function resolveCell<T>(cell: SettingsCell<T>): T {
  const ov = cell.override.value;
  return ov === undefined ? cell.base.value : ov;
}

/**
 * Pose for the view's camera. position is world-space; target is what the
 * camera looks at; up defaults to Z-up (matching the rest of the codebase).
 */
export interface CameraPose {
  position: THREE.Vector3;
  target: THREE.Vector3;
  up?: THREE.Vector3;
}

/**
 * Per-view enable/disable for scene-level clip volumes (boxes, planes, surfaces,
 * polygon clip volumes). Keyed by the volume's `identifier`. Missing keys are
 * treated as enabled — so adding a new clip volume to the scene defaults to
 * "visible in this view". Set a key to `false` to hide that volume's clip
 * effect from this view.
 */
export type SceneClipMask = { readonly [identifier: string]: boolean };

/**
 * The full bag of layered settings on a RenderView.
 *
 * Adding a new setting: declare a cell here, give it a sensible base in the
 * factory below, and read it in `resolveSettings`. The widget's UI picks it
 * up automatically by binding to `cell.base` / `cell.override`.
 */
export interface RenderViewSettingsBag {
  /** Active attribute (e.g. 'rgba', 'elevation'). Empty string = no override. */
  activeAttributeName: SettingsCell<string | null>;
  /** EDL on/off. Independent of the pipeline kind — a Potree pipeline ignores this. */
  useEDL: SettingsCell<boolean>;
  /** Point size override. `undefined` base means "use the material's intrinsic size". */
  size: SettingsCell<number | undefined>;
  /** Point shape (THREE.PointShape value). `undefined` base means "use intrinsic". */
  shape: SettingsCell<number | undefined>;
  /**
   * Per-view enable mask for scene-level clip volumes. Default `{}` means
   * "all enabled" (since missing keys default to enabled).
   */
  sceneClipMask: SettingsCell<SceneClipMask>;
}

/**
 * Empty/default settings bag. Every cell has `undefined`/sentinel base and
 * `undefined` override, which `resolveSettings` interprets as "don't override".
 *
 * The main view uses this bag — its render path is therefore byte-identical
 * to before the layered settings were introduced.
 */
export function makeDefaultSettingsBag(): RenderViewSettingsBag {
  return {
    activeAttributeName: makeCell<string | null>(null),
    useEDL: makeCell<boolean>(false),
    size: makeCell<number | undefined>(undefined),
    shape: makeCell<number | undefined>(undefined),
    sceneClipMask: makeCell<SceneClipMask>({}),
  };
}

/**
 * The output of resolving a settings bag. Maps directly onto the existing
 * MaterialOverride / ClipOverride shapes consumed by the render loop.
 *
 * Cells whose resolved value is `null`/sentinel are omitted from
 * `materialOverride` so applyOverrides() doesn't snapshot/restore them.
 */
export interface ResolvedRenderViewSettings {
  materialOverride: MaterialOverride;
  clipOverride: ClipOverride | null;
}

/**
 * Resolve a settings bag into the per-frame override shapes.
 *
 * Conventions:
 * - `activeAttributeName` cell with `null` resolved value means "no override"
 *   (since the underlying material field is also nullable, we use `null` as
 *   the sentinel for "not set").
 * - `useEDL` is non-nullable; the override only kicks in if `override` is
 *   defined (so we check the cell's override directly, not the resolved value).
 * - `size`/`shape` use `undefined` to mean "no override".
 * - `sceneClipMask`: if any key resolves to `false`, the view gets a
 *   `clipOverride: replace` carrying the base clip state filtered by the
 *   mask. Empty mask (all-true) leaves `clipOverride: null` so the main
 *   view's render path is identical to before.
 *
 * `baseClipState` is the scene-derived clip state computed once per frame;
 * passed in so the resolver can produce a filtered variant for masked views.
 */
export function resolveSettings(bag: RenderViewSettingsBag, baseClipState: ClipState): ResolvedRenderViewSettings {
  const materialOverride: MaterialOverride = {};

  // activeAttributeName: only override if a non-null value is in play and
  // override is explicitly set. Null base + null override = no override.
  const attrOverride = bag.activeAttributeName.override.value;
  if (attrOverride !== undefined) {
    materialOverride.activeAttributeName = attrOverride;
  } else if (bag.activeAttributeName.base.value !== null) {
    materialOverride.activeAttributeName = bag.activeAttributeName.base.value;
  }

  // useEDL: only override if cell.override is set OR base differs from "unset".
  // We treat the bag's default `false` as "no override", which matches the
  // main view's behavior (it never set useEDL via the override path before).
  if (bag.useEDL.override.value !== undefined) {
    materialOverride.useEDL = bag.useEDL.override.value;
  }
  // (Base useEDL is intentionally not pushed onto materialOverride; the
  // pipeline drives base EDL via its own logic. Override is the way in.)

  const sizeResolved = resolveCell(bag.size);
  if (sizeResolved !== undefined) materialOverride.size = sizeResolved;

  const shapeResolved = resolveCell(bag.shape);
  if (shapeResolved !== undefined) materialOverride.shape = shapeResolved;

  // Scene clip mask: only build a clipOverride if the resolved mask actually
  // disables anything. Empty mask = no behavior change.
  let clipOverride: ClipOverride | null = null;
  const mask = resolveCell(bag.sceneClipMask);
  const hasDisabled = Object.values(mask).some((v) => v === false);
  if (hasDisabled) {
    clipOverride = { mode: 'replace', state: filterClipStateByMask(baseClipState, mask) };
  }

  return { materialOverride, clipOverride };
}
